import { useEffect, useRef } from "react";
import { industries } from "@/mocks/industries";

export default function Industries() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = cardsRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        card.classList.add("opacity-0", "translate-y-8", "transition-all", "duration-700");
        (card as HTMLElement).style.transitionDelay = `${index * 100}ms`;
        observer.observe(card);
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="industries" className="py-20 md:py-28 bg-dark-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-accent-50 text-accent-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-accent-200">
              Kome služimo
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-dark-900 mb-4">
              Bezbednosna rešenja za
              <br />
              <span className="text-brand-500">svaki tip objekta</span>
            </h2>
            <p className="text-dark-400 text-lg max-w-2xl mx-auto">
              Bez obzira da li štitite porodični dom ili industrijski kompleks, imamo rešenje za vas
            </p>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {industries.map((item, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl border border-dark-100 overflow-hidden hover:-translate-y-1 transition-all duration-300 flex flex-col sm:flex-row"
              >
                <div className="sm:w-2/5 bg-dark-100 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="sm:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-brand-50 rounded-xl flex-shrink-0 group-hover:bg-brand-500 transition-all">
                      <i className={`${item.icon} text-lg text-brand-500 group-hover:text-white transition-all`}></i>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-dark-400 uppercase tracking-wider">
                        {item.subtitle}
                      </span>
                      <h3 className="text-lg font-bold text-dark-900">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-dark-500 text-sm leading-relaxed mb-4">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {item.features.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs text-dark-400 bg-dark-50 px-2.5 py-1 rounded-full">
                        <i className="ri-check-line text-brand-500 text-xs"></i>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}